// Browser storage for the setup wizard. The server (`ai.setup`) is the source of truth; these
// keys only make reloads instant and keep the dashboard gate fast. The key names are shared with
// the dashboard gate (ai-employee/index.tsx) and the specs, so keep them as they are.
import type { PendingSave, SetupDraft, SetupStateResponse, SetupStep } from './types.ts';
import { SETUP_STEPS } from './types.ts';

/** localStorage `ai-onboarding-completed-<businessId>` = 'true' once setup was completed or skipped. */
export const ONBOARDING_COMPLETED_PREFIX = 'ai-onboarding-completed-';
/** sessionStorage `ai-onboarding-progress-<businessId>`: this tab's {@link ProgressMirror}. */
export const WIZARD_PROGRESS_PREFIX = 'ai-onboarding-progress-';
/**
 * sessionStorage `ai-setup-exited-<businessId>`: the owner chose "Exit setup" in this tab, so the
 * dashboard should not send them straight back to /ai/start while setup is still in progress.
 */
export const SETUP_EXITED_PREFIX = 'ai-setup-exited-';

/** This tab's copy of the wizard, so a reload renders straight away. Version 1 copies are ignored. */
export interface ProgressMirror {
  version: 2;
  step: SetupStep;
  maxStep: SetupStep;
  runId: string | null;
  draft: SetupDraft;
  /** Last setup state seen (with the latest run folded into `latestRun`). */
  state: SetupStateResponse | null;
  /** Step changes not saved yet: a reload sends them. Absent from older copies. */
  pendingSaves: PendingSave[];
  savedAt: number;
}

const isStep = (value: unknown): value is SetupStep => SETUP_STEPS.includes(value as SetupStep);

/** Saves read back from a saved copy; anything malformed is dropped. */
export function pendingSavesFrom(value: unknown): PendingSave[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((save) => save && isStep(save.from) && isStep(save.to))
    .map((save) => (save.leaving === true ? { from: save.from, to: save.to, leaving: true } : { from: save.from, to: save.to }));
}

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string | null) {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode) or full; the server copy still resumes setup.
  }
}

const session = () => sessionStorage;
const local = () => localStorage;

export function clearWizardProgress(businessId: string) {
  write(session, `${WIZARD_PROGRESS_PREFIX}${businessId}`, null);
}

export function loadWizardProgress(businessId: string): ProgressMirror | null {
  const raw = read(session, `${WIZARD_PROGRESS_PREFIX}${businessId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ProgressMirror>;
    if (parsed?.version !== 2 || !isStep(parsed.step) || !parsed.draft || typeof parsed.draft !== 'object')
      return null;
    return {
      version: 2,
      step: parsed.step,
      maxStep: isStep(parsed.maxStep) ? parsed.maxStep : parsed.step,
      runId: typeof parsed.runId === 'string' ? parsed.runId : null,
      draft: parsed.draft as SetupDraft,
      state: parsed.state && typeof parsed.state === 'object' ? parsed.state : null,
      pendingSaves: pendingSavesFrom(parsed.pendingSaves),
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
    };
  } catch {
    return null;
  }
}

export function saveWizardProgress(businessId: string, mirror: Omit<ProgressMirror, 'version' | 'savedAt'>) {
  const value: ProgressMirror = { version: 2, ...mirror, savedAt: Date.now() };
  try {
    write(session, `${WIZARD_PROGRESS_PREFIX}${businessId}`, JSON.stringify(value));
  } catch {
    // A state that can't be serialised is simply not mirrored.
  }
}

export function isSetupMarkedCompleted(businessId: string): boolean {
  return read(local, `${ONBOARDING_COMPLETED_PREFIX}${businessId}`) === 'true';
}

/** Only for a completed or skipped setup: the gate then stops sending this browser to /ai/start. */
export function markSetupCompleted(businessId: string) {
  write(local, `${ONBOARDING_COMPLETED_PREFIX}${businessId}`, 'true');
}

export function markSetupExited(businessId: string) {
  write(session, `${SETUP_EXITED_PREFIX}${businessId}`, 'true');
}

export function wasSetupExited(businessId: string): boolean {
  return read(session, `${SETUP_EXITED_PREFIX}${businessId}`) === 'true';
}
